import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useAuthorizations } from 'hooks';
import { Box, Checkbox } from '@mui/material';
import { CloudDownload, History, BorderColor, Close } from '@mui/icons-material';
import { emptyArray, emptyString } from 'constants/defaultValues';
import { Tr, Td } from 'react-super-responsive-table';
import useConfirmDialog from 'features/common/hooks/useConfirmDialog';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { Autocomplete, IconButton } from '@totalsoft/rocket-ui';
import { PrintLink } from './PrintStyles';

const PrintComponent = ({
  print,
  index,
  onPrintDocument,
  onSelectPrint,
  onSignatureTypeChange,
  onDownloadFile,
  onOpenHistoryModal,
  onResetDocumentSigning,
  readOnly
}) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const authorizations = useAuthorizations(
    [
      AuthorizationCodes.hasRightToSignDocuments,
      AuthorizationCodes.hasRightToResetDocumentSigning,
      AuthorizationCodes.hasRightToPrintDocuments
    ],
    documentTypeId,
    documentStateId
  );
  const { t } = useTranslation();
  const confirm = useConfirmDialog();

  const handleSelectPrint = useCallback(() => onSelectPrint(print.documentPrintId), [onSelectPrint, print]);

  const handlePrintDocument = useCallback(() => onPrintDocument(print.documentPrintId), [onPrintDocument, print]);

  const handleResetSigning = useCallback(
    () =>
      confirm(() => onResetDocumentSigning(print.documentPrintId), {
        title: t('Offer.Edit.PrintData.ResetSigning.Title'),
        content: t('Offer.Edit.PrintData.ResetSigning.Content')
      }),
    [confirm, onResetDocumentSigning, print, t]
  );

  const handleSignatureTypeChange = useCallback(id => onSignatureTypeChange(print.documentPrintId, id), [onSignatureTypeChange, print]);

  const handleDownloadFile = useCallback(() => onDownloadFile(print.fileUniqueId), [onDownloadFile, print]);

  const handleOpenHistoryModal = useCallback(() => onOpenHistoryModal(print), [onOpenHistoryModal, print]);

  return (
    <Tr>
      <Td>
        <Checkbox
          id={`print_selection_${index}`}
          disabled={readOnly({
            controlName: 'Documents',
            hasAuthorization: authorizations?.hasRightToSignDocuments || authorizations?.hasRightToPrintDocuments
          })}
          checked={print.selected}
          onChange={handleSelectPrint}
        />
      </Td>
      <Td>
        {print.needsSigning && (
          <IconButton disabled tooltip={t('Offer.Edit.PrintData.NeedsSigning')} size='small' variant='text'>
            <BorderColor fontSize='small' />
          </IconButton>
        )}
      </Td>
      <Td>
        <PrintLink onClick={handlePrintDocument}>{print.templateName}</PrintLink>
      </Td>
      <Td>{print.entityTitle}&nbsp;</Td>
      <Td>{print.userName ? `${print.userName} / ${t('DATE', { date: print.uploadedDate })}` : null}</Td>
      <Td>
        {print.signStatusName}
        {print.signStatusName && authorizations?.hasRightToResetDocumentSigning && (
          <IconButton size='small' tooltip={t('Offer.Edit.PrintData.ResetSign')} onClick={handleResetSigning}>
            <Close />
          </IconButton>
        )}{' '}
        &nbsp;
      </Td>
      <Td>
        {`${t('DATE', { date: print.signatureDate })} ${
          print.signatureDate ? new Date(print.signatureDate).toLocaleTimeString() : emptyString
        }`}
      </Td>
      <Td>
        {print.templateForSign && (
          <Autocomplete
            id={`Template_SignatureType_${print.templateId}`}
            fullWidth
            required={print.selected}
            disabled={readOnly({
              controlName: 'Documents.SignatureType',
              hasAuthorization: authorizations?.hasRightToSignDocuments
            })}
            isSearchable={true}
            isClearable={false}
            simpleValue={true}
            labelKey='code'
            value={print.signatureTypeId}
            onChange={handleSignatureTypeChange}
            options={print.signatureTypes || emptyArray}
            error={print.selected && !print.signatureTypeId}
          />
        )}
      </Td>
      <Td>
        <Box sx={{ textAlign: 'right' }}>
          <IconButton
            id={`print_Export${index}`}
            onClick={handleDownloadFile}
            size='small'
            variant='text'
            tooltip={t('Tooltips.Download')}
            disabled={!print.fileUniqueId}
          >
            <CloudDownload fontSize='small' />
          </IconButton>
          <IconButton
            id={`print_History${index}`}
            onClick={handleOpenHistoryModal}
            size='small'
            variant='text'
            tooltip={t('Tooltips.History')}
            disabled={!print.fileUniqueId}
          >
            <History fontSize='small' />
          </IconButton>
        </Box>
      </Td>
    </Tr>
  );
};

PrintComponent.propTypes = {
  print: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onPrintDocument: PropTypes.func.isRequired,
  onSelectPrint: PropTypes.func.isRequired,
  onSignatureTypeChange: PropTypes.func.isRequired,
  onDownloadFile: PropTypes.func.isRequired,
  onOpenHistoryModal: PropTypes.func.isRequired,
  onResetDocumentSigning: PropTypes.func.isRequired,
  readOnly: PropTypes.func.isRequired
};

export default PrintComponent;
